/**
 * /downhole — the Downhole Tools dashboard (fieldiq-api#882 `GET /dashboard`,
 * `GET /components/alerts`).
 *
 * EVERY FIGURE IS THE API'S. The counts come from `/dashboard`, the alert list
 * from `/components/alerts`, and nothing here tallies, re-thresholds or sums.
 * The unrouted version filtered the alert list again on a `wear_percent` key
 * the API never sends, so `undefined >= 85` was always false and the panel
 * never rendered; it would also have dropped every inspection-overdue alert.
 * The API has already cut the list at its threshold and says why each row is
 * on it (`reasons[]`).
 *
 * A dashboard: no back arrow (CLAUDE.md, Other rules).
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ClipboardList, Layers, Loader2, Wrench } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeComponentAlerts, useDownholeDashboard } from '../../hooks/useDownhole';
import { fmtCurrency } from '../../lib/display-labels';
import {
  DH_ALERT_REASON_LABEL,
  DH_ASSEMBLY_STATUS_SKIN,
  DH_COMPONENT_STATUS_SKIN,
  dhLabel,
} from '../../lib/downhole-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { formatInstantDay } from '../../lib/calendar-date';
import { DH_ASSEMBLY_STATUSES, DH_COMPONENT_STATUSES } from '../../types/downhole';
import { DhBadge, DhCategoryPill, DhEmpty, DhPanel, DhWearMeter } from './DownholeBits';

export default function DashboardScreen() {
  const navigate = useNavigate();
  const dashboardQuery = useDownholeDashboard();
  const alertsQuery = useDownholeComponentAlerts();

  const dashboard = dashboardQuery.data?.data ?? null;
  const dashboardFailure = queryLoadFailure(dashboardQuery);
  // A stat is masked while loading AND when the read failed: a failed read has
  // no figures, and `displayValue(undefined)` would only say so as a dash.
  const statsLoading = dashboardQuery.isLoading;

  const alerts = alertsQuery.data?.data ?? [];
  const alertsMeta = alertsQuery.data?.meta;
  const alertsFailure = queryLoadFailure(alertsQuery);

  return (
    <div data-testid="downhole-dashboard-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Downhole tools"
        title="Tool Dashboard"
        stats={[
          {
            icon: <Wrench className="size-4" aria-hidden="true" />,
            label: 'Components',
            sub: 'In the register',
            tone: 'neutral',
            loading: statsLoading,
            value: displayValue(dashboard?.total_components),
          },
          {
            icon: <Layers className="size-4" aria-hidden="true" />,
            label: 'Assemblies',
            sub: 'Building to in hole',
            tone: 'info',
            loading: statsLoading,
            value: displayValue(dashboard?.active_assemblies),
          },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Alerts',
            sub: 'To review',
            tone: 'accent',
            loading: statsLoading,
            value: displayValue(dashboard?.wear_alerts),
          },
          {
            icon: <ClipboardList className="size-4" aria-hidden="true" />,
            label: 'Work orders',
            sub: 'Open',
            tone: 'neutral',
            loading: statsLoading,
            value: displayValue(dashboard?.open_work_orders),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {dashboardFailure ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice
                error={queryFailureError(dashboardQuery)}
                kind={dashboardFailure}
                subject="the downhole dashboard"
              />
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              <StatusBreakdown
                eyebrow="Component register"
                title="Components by status"
                loading={statsLoading}
                rows={DH_COMPONENT_STATUSES.map((s) => ({
                  key: s,
                  value: dashboard?.components_by_status?.[s],
                  onOpen: () => navigate(`/downhole/components?status=${s}`),
                }))}
                skin={DH_COMPONENT_STATUS_SKIN}
              />
              <StatusBreakdown
                eyebrow="Assemblies"
                title="Assemblies by status"
                loading={statsLoading}
                rows={DH_ASSEMBLY_STATUSES.map((s) => ({ key: s, value: dashboard?.assemblies_by_status?.[s] }))}
                skin={DH_ASSEMBLY_STATUS_SKIN}
                footer={
                  dashboard && (
                    <>
                      Outstanding invoices: {displayValue(dashboard.pending_invoices)} ·{' '}
                      {/* NULL when the outstanding invoices are in more than one
                          currency: a sum across CAD and USD is not a figure. */}
                      {dashboard.outstanding_invoice_total == null
                        ? '— (more than one currency)'
                        : fmtCurrency(dashboard.outstanding_invoice_total, dashboard.outstanding_invoice_currency)}
                    </>
                  )
                }
              />
            </div>
          )}

          <DhPanel
            eyebrow="Needs attention"
            title="Component alerts"
            testId="downhole-alerts"
            footer={
              alertsMeta?.threshold != null && !alertsFailure
                ? `Wear at or past ${alertsMeta.threshold}% of a limit, or an inspection past due on ${alertsMeta.today ?? 'today'}. Retired components are excluded.`
                : undefined
            }
          >
            {alertsQuery.isLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading alerts" />
              </div>
            ) : alertsFailure ? (
              <LoadFailureNotice error={queryFailureError(alertsQuery)} kind={alertsFailure} subject="component alerts" />
            ) : alerts.length === 0 ? (
              <DhEmpty>No component is near a wear limit or past an inspection date.</DhEmpty>
            ) : (
              <ul className="divide-y divide-fieldiq-border">
                {alerts.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/downhole/components/${a.id}`}
                      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
                    >
                      <span className="min-w-[9rem] font-mono text-sm font-extrabold text-fieldiq-accent-signal">{a.serial_number}</span>
                      <DhCategoryPill category={a.category} />
                      <DhBadge map={DH_COMPONENT_STATUS_SKIN} value={a.status} />
                      <span className="flex flex-wrap gap-1.5">
                        {a.reasons.map((r) => (
                          <span
                            key={r}
                            className="rounded-fi-pill border border-fieldiq-border px-2 py-0.5 text-[0.68rem] font-bold text-fieldiq-text"
                          >
                            {dhLabel(DH_ALERT_REASON_LABEL, r)}
                          </span>
                        ))}
                      </span>
                      <span className="ml-auto flex items-center gap-4">
                        {a.inspection_overdue && (
                          <span className="text-xs text-fieldiq-text">
                            Due {formatInstantDay(a.next_inspection_due, alertsMeta?.time_zone) ?? '—'}
                          </span>
                        )}
                        <DhWearMeter percent={a.wear_percent_max} label="Wear used, the worse of hours and runs" />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </DhPanel>
        </div>
      </section>
    </div>
  );
}

/** One status per line with the API's count. A row opens the filtered register when it can. */
function StatusBreakdown({
  eyebrow,
  title,
  rows,
  skin,
  loading,
  footer,
}: {
  eyebrow: string;
  title: string;
  rows: { key: string; value: number | undefined; onOpen?: () => void }[];
  skin: React.ComponentProps<typeof DhBadge>['map'];
  loading: boolean;
  footer?: React.ReactNode;
}) {
  return (
    <DhPanel eyebrow={eyebrow} title={title} footer={footer}>
      <ul className="divide-y divide-fieldiq-border">
        {rows.map((r) => {
          const body = (
            <>
              <DhBadge map={skin} value={r.key} />
              {/* While loading there is no count to show and none is claimed. */}
              <span className="font-mono text-sm font-extrabold tabular-nums text-white">
                {loading ? '' : displayValue(r.value)}
              </span>
            </>
          );
          return (
            <li key={r.key}>
              {r.onOpen ? (
                <button
                  type="button"
                  onClick={r.onOpen}
                  className="flex w-full items-center justify-between px-4 py-2.5 text-left outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
                >
                  {body}
                </button>
              ) : (
                <div className="flex items-center justify-between px-4 py-2.5">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </DhPanel>
  );
}
